// src/components/issues/ZoneDetailModal.js
import React, { useState, useEffect, useMemo, memo } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton,
  Tabs, TabList, TabPanels, Tab, TabPanel, Box, Heading, Text, Flex, SimpleGrid,
  Stat, StatLabel, StatNumber, StatHelpText, Badge, VStack, HStack, Icon,
  useColorModeValue, Divider, Progress, Skeleton, Circle, Button, Alert,
  AlertIcon, AlertTitle, AlertDescription, Input, InputGroup, InputLeftElement
} from '@chakra-ui/react';
import { 
  MdMap, MdErrorOutline, MdCheckCircle, MdEngineering, 
  MdWarning, MdWaterDrop, MdBolt, MdAssignment, MdOutlineWaterDrop,
  MdWaves, MdTimeline, MdSensors, MdSettingsEthernet, MdOpacity,
  MdSearch, MdDownload, MdArrowForward
} from 'react-icons/md';
import { motion, AnimatePresence } from 'framer-motion';
import * as IssueService from '../../services/issueService';
import { formatDistanceToNowStrict } from 'date-fns';

const MotionBox = motion(Box);
const MotionVStack = motion(VStack);

// ============================================================================
// CONFIGURATION & MAPPINGS
// ============================================================================
const ASSET_CONFIG = {
  well: { icon: MdOpacity, color: 'teal', label: 'Well' },
  pump: { icon: MdSettingsEthernet, color: 'blue', label: 'Pump Station' },
  pipeline: { icon: MdTimeline, color: 'gray', label: 'Pipeline' },
  sensor: { icon: MdSensors, color: 'purple', label: 'IoT Sensor' },
  tank: { icon: MdWaterDrop, color: 'cyan', label: 'Storage Tank' },
  default: { icon: MdEngineering, color: 'gray', label: 'Infrastructure' }
};

const ISSUE_TYPE_CONFIG = {
  flooding: { color: 'blue', icon: MdWaves, label: 'Flooding' },
  leak: { color: 'cyan', icon: MdOutlineWaterDrop, label: 'Leak' },
  blockage: { color: 'orange', icon: MdSettingsEthernet, label: 'Blockage' },
  damage: { color: 'red', icon: MdWarning, label: 'Damage' },
  malfunction: { color: 'purple', icon: MdBolt, label: 'Malfunction' },
  spillage: { color: 'teal', icon: MdWaterDrop, label: 'Spillage' },
  other: { color: 'gray', icon: MdErrorOutline, label: 'Other' },
};

const STATUS_COLORS = {
  open: 'red',
  pending_assignment: 'orange',
  in_progress: 'blue',
  resolved: 'green',
  closed: 'gray',
  operational: 'green',
  maintenance_required: 'orange',
  damaged: 'red',
  offline: 'gray'
};

// ============================================================================
// ANIMATION VARIANTS
// ============================================================================
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.2 } }
};

// ============================================================================
// SUB-COMPONENTS (Hooks declared at the top)
// ============================================================================

const EmptyState = memo(({ icon, title, message }) => {
  const circleBg = useColorModeValue('gray.50', 'gray.700');
  const titleColor = useColorModeValue('gray.700', 'white');
  
  return (
    <Flex direction="column" align="center" justify="center" py={10} px={4} textAlign="center">
      <Circle size="60px" bg={circleBg} mb={4}>
        <Icon as={icon} boxSize={6} color="gray.400" />
      </Circle>
      <Text fontWeight="600" fontSize="md" color={titleColor}>{title}</Text>
      <Text fontSize="sm" color="gray.500" mt={1} maxW="300px">{message}</Text>
    </Flex>
  );
});

const LoadingSkeleton = memo(() => (
  <VStack spacing={6} align="stretch" w="100%">
    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
      {[...Array(4)].map((_, i) => <Skeleton key={i} h="100px" borderRadius="xl" />)}
    </SimpleGrid>
    <Box>
      <Skeleton h="24px" w="150px" mb={4} />
      <VStack spacing={3}>
        {[...Array(3)].map((_, i) => <Skeleton key={i} h="40px" w="100%" borderRadius="lg" />)}
      </VStack>
    </Box>
  </VStack>
));

const IssueRow = memo(({ issue }) => {
  const cfg = ISSUE_TYPE_CONFIG[issue.issue_type?.toLowerCase()] || ISSUE_TYPE_CONFIG.other;
  const statusColor = STATUS_COLORS[issue.status?.toLowerCase()] || 'gray';
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const rowBg = useColorModeValue('white', 'gray.800');
  const hoverBg = useColorModeValue('gray.50', 'gray.750');
  
  return (
    <MotionBox variants={itemVariants}>
      <Flex 
        p={4} 
        borderWidth="1px" 
        borderColor={borderColor} 
        borderRadius="xl" 
        align="center" 
        justify="space-between"
        bg={rowBg}
        _hover={{ bg: hoverBg, boxShadow: 'sm', transform: 'translateY(-1px)' }}
        transition="all 0.2s"
      >
        <HStack spacing={4}>
          <Circle size="40px" bg={`${cfg.color}.100`}>
            <Icon as={cfg.icon} color={`${cfg.color}.500`} boxSize={5} />
          </Circle>
          <VStack align="start" spacing={0}>
            <HStack>
              <Text fontWeight="700" fontSize="sm">Issue #{issue.id}</Text>
              {issue.severity === 'critical' && <Badge colorScheme="red" fontSize="2xs">CRITICAL</Badge>}
              {issue.severity === 'high' && <Badge colorScheme="orange" fontSize="2xs">HIGH</Badge>}
            </HStack>
            <Text fontSize="xs" color="gray.500" noOfLines={1} maxW={{ base: "200px", md: "350px" }}>
              {issue.description || 'No description provided'}
            </Text>
          </VStack>
        </HStack>
        <VStack align="end" spacing={1}>
          <Badge colorScheme={statusColor} fontSize="2xs" borderRadius="md" px={2} py={0.5}>
            {issue.status?.replace(/_/g, ' ').toUpperCase()}
          </Badge>
          <Text fontSize="2xs" color="gray.400" fontWeight="500">
            {issue.reported_at ? formatDistanceToNowStrict(new Date(issue.reported_at), { addSuffix: true }) : 'Unknown'}
          </Text>
        </VStack>
      </Flex>
    </MotionBox>
  );
});

const AssetRow = memo(({ asset }) => {
  const cfg = ASSET_CONFIG[asset.type?.toLowerCase()] || ASSET_CONFIG.default;
  const statusColor = STATUS_COLORS[asset.status?.toLowerCase()] || 'gray';
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const rowBg = useColorModeValue('white', 'gray.800');
  const hoverBg = useColorModeValue('gray.50', 'gray.750');

  return (
    <MotionBox variants={itemVariants}>
      <Flex 
        p={4} 
        borderWidth="1px" 
        borderColor={borderColor} 
        borderRadius="xl" 
        align="center" 
        justify="space-between"
        bg={rowBg}
        _hover={{ bg: hoverBg, boxShadow: 'sm', transform: 'translateY(-1px)' }}
        transition="all 0.2s"
      >
        <HStack spacing={4}>
          <Circle size="40px" bg={`${cfg.color}.100`}>
            <Icon as={cfg.icon} color={`${cfg.color}.500`} boxSize={5} />
          </Circle>
          <VStack align="start" spacing={0}>
            <Text fontWeight="700" fontSize="sm">{asset.name || `Asset #${asset.id}`}</Text>
            <HStack divider={<Text color="gray.300">•</Text>} spacing={2}>
              <Text fontSize="xs" color="gray.500" fontWeight="600">{cfg.label}</Text>
              <Text fontSize="xs" color="gray.500">{asset.manufacturer || 'Standard Build'}</Text>
            </HStack>
          </VStack>
        </HStack>
        <Badge colorScheme={statusColor} fontSize="2xs" borderRadius="md" px={2} py={0.5}>
          {asset.status?.replace(/_/g, ' ').toUpperCase() || 'UNKNOWN'}
        </Badge>
      </Flex>
    </MotionBox>
  );
});

const WorkOrderRow = memo(({ wo }) => {
  const statusColor = STATUS_COLORS[wo.status?.toLowerCase()] || 'gray';
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const rowBg = useColorModeValue('white', 'gray.800');
  const hoverBg = useColorModeValue('gray.50', 'gray.750');

  return (
    <MotionBox variants={itemVariants}>
      <Flex 
        p={4} 
        borderWidth="1px" 
        borderColor={borderColor} 
        borderRadius="xl" 
        align="center" 
        justify="space-between"
        bg={rowBg}
        _hover={{ bg: hoverBg, boxShadow: 'sm', transform: 'translateY(-1px)' }}
        transition="all 0.2s"
      >
        <HStack spacing={4}>
          <Circle size="40px" bg="blue.100">
            <Icon as={MdAssignment} color="blue.500" boxSize={5} />
          </Circle>
          <VStack align="start" spacing={0}>
            <Text fontWeight="700" fontSize="sm" noOfLines={1} maxW={{ base: "200px", md: "350px" }}>
              {wo.title || `Work Order #${wo.id}`}
            </Text>
            <Text fontSize="xs" color="gray.500" fontWeight="500">
              Assigned to: {wo.assigned_to_name || 'Unassigned'}
            </Text>
          </VStack>
        </HStack>
        <VStack align="end" spacing={1}>
          <Badge colorScheme={statusColor} fontSize="2xs" borderRadius="md" px={2} py={0.5}>
            {wo.status?.replace(/_/g, ' ').toUpperCase()}
          </Badge>
          {(wo.priority === 'high' || wo.priority === 'critical') && (
            <Badge colorScheme="red" variant="outline" fontSize="2xs">PRIORITY</Badge>
          )}
        </VStack>
      </Flex>
    </MotionBox>
  );
});

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const ZoneDetailModal = ({ isOpen, onClose, zone }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [zoneData, setZoneData] = useState(null);
  const [error, setError] = useState(null);

  // Search states for tabs
  const [issueSearch, setIssueSearch] = useState('');
  const [assetSearch, setAssetSearch] = useState('');

  // 🚀 ALL HOOKS MUST BE DECLARED AT THE TOP LEVEL BEFORE ANY EARLY RETURNS
  const bg = useColorModeValue('white', 'navy.800');
  const border = useColorModeValue('gray.200', 'navy.700');
  const textMuted = useColorModeValue('gray.500', 'gray.400');
  const scrollBg = useColorModeValue('gray.50', 'navy.900');
  const headerBg = useColorModeValue('white', 'navy.800');
  const statRedBg = useColorModeValue('red.50', 'red.900');
  const progressBg = useColorModeValue('gray.100', 'gray.700');
  
  // 🚀 Fetch comprehensive zone data from backend on mount
  useEffect(() => {
    let isMounted = true;
    
    if (isOpen && zone?.id) {
      setIsLoading(true);
      setError(null);
      
      // Call the IssueService to get full geographical analytics
      if (IssueService.fetchZoneAnalytics) {
          IssueService.fetchZoneAnalytics(zone.id)
            .then(data => {
              if (isMounted) {
                setZoneData(data);
                setIsLoading(false);
              }
            })
            .catch(err => {
              if (isMounted) {
                console.error("Failed to load zone analytics:", err);
                setError("Failed to load comprehensive zone data from the server.");
                setIsLoading(false);
              }
            });
      } else {
          // Fallback simulation if backend endpoint isn't fully ready
          setTimeout(() => {
            if (isMounted) {
                setZoneData({
                  kpis: {
                      total_assets: Math.floor(Math.random() * 50) + 120,
                      active_wos: Math.floor(Math.random() * 10) + 5,
                      critical_alerts: Math.floor(Math.random() * 3),
                      health_score: (Math.random() * 15 + 80).toFixed(1),
                  },
                  issue_composition: [
                    { type: 'Flooding', count: Math.floor(zone.count * 0.4), color: 'blue' },
                    { type: 'Pipeline Leak', count: Math.floor(zone.count * 0.3), color: 'cyan' },
                    { type: 'Valve Failure', count: Math.floor(zone.count * 0.2), color: 'orange' },
                    { type: 'Pressure Drop', count: Math.floor(zone.count * 0.1), color: 'red' }
                  ],
                  issues: Array.from({ length: zone.count }).map((_, i) => ({
                      id: 1000 + i,
                      issue_type: ['flooding', 'leak', 'blockage', 'damage'][i % 4],
                      description: 'System detected anomaly in sector ' + (i + 1),
                      status: i % 3 === 0 ? 'in_progress' : 'open',
                      severity: i === 0 ? 'critical' : 'medium',
                      reported_at: new Date(Date.now() - (i * 3600000)).toISOString()
                  })),
                  assets: Array.from({ length: 15 }).map((_, i) => ({
                      id: 5000 + i,
                      type: ['well', 'pump', 'pipeline', 'tank'][i % 4],
                      name: `Sector ${zone.area.split(' ')[0]} ${['Well', 'Pump', 'Main', 'Storage'][i % 4]} ${i+1}`,
                      status: i % 5 === 0 ? 'maintenance_required' : 'operational',
                      manufacturer: 'FlowSense Ind.'
                  })),
                  work_orders: Array.from({ length: 5 }).map((_, i) => ({
                      id: 8000 + i,
                      title: `Emergency Repair at Zone ${zone.id}`,
                      status: i % 2 === 0 ? 'in_progress' : 'assigned',
                      priority: i === 0 ? 'high' : 'medium',
                      assigned_to_name: `Technician ${i + 1}`
                  }))
                });
                setIsLoading(false);
            }
          }, 800);
      }
    }

    return () => { isMounted = false; };
  }, [isOpen, zone]);

  // 🚀 ONLY AFTER ALL HOOKS HAVE BEEN DECLARED, WE DO THE EARLY RETURN
  if (!zone) return null;

  const { kpis, issues = [], assets = [], work_orders = [], issue_composition = [] } = zoneData || {};

  // Filters
  const filteredIssues = issues.filter(iss => 
      iss.description?.toLowerCase().includes(issueSearch.toLowerCase()) || 
      iss.issue_type?.toLowerCase().includes(issueSearch.toLowerCase())
  );

  const filteredAssets = assets.filter(ast => 
      ast.name?.toLowerCase().includes(assetSearch.toLowerCase()) || 
      ast.type?.toLowerCase().includes(assetSearch.toLowerCase())
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="5xl" motionPreset="slideInBottom" scrollBehavior="inside">
      <ModalOverlay backdropFilter="blur(6px)" bg="blackAlpha.600" />
      <ModalContent bg={bg} borderRadius="2xl" overflow="hidden" maxH="90vh" boxShadow="2xl">
        
        {/* Dynamic Top Border */}
        <Box h="5px" bg={`${zone.color}.400`} />
        
        <ModalHeader borderBottomWidth="1px" borderColor={border} pb={4} bg={headerBg}>
          <Flex justify="space-between" align="center" pr={8}>
              <HStack spacing={4}>
                <Circle size="48px" bg={`${zone.color}.50`} boxShadow="sm">
                  <Icon as={MdMap} color={`${zone.color}.500`} boxSize={6} />
                </Circle>
                <VStack align="start" spacing={0}>
                  <HStack>
                    <Heading size="md" fontWeight="800">{zone.area} Operations Zone</Heading>
                    <Badge colorScheme={zone.color} borderRadius="full" px={2}>{zone.id}</Badge>
                  </HStack>
                  <Text fontSize="sm" color={textMuted} fontWeight="500">
                    Real-time geographic and infrastructure analysis
                  </Text>
                </VStack>
              </HStack>
              
              <Button 
                size="sm" 
                variant="outline" 
                colorScheme="blue" 
                leftIcon={<Icon as={MdDownload} />}
                borderRadius="lg"
              >
                Export Zone Data
              </Button>
          </Flex>
        </ModalHeader>
        <ModalCloseButton mt={2} />

        <ModalBody p={0} bg={scrollBg}>
          {error ? (
            <Alert status="error" m={6} borderRadius="xl" w="auto">
              <AlertIcon />
              <Box>
                <AlertTitle>Connection Error</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Box>
            </Alert>
          ) : (
            <Tabs colorScheme={zone.color} isLazy variant="line">
              <TabList px={6} pt={4} borderBottomWidth="1px" borderColor={border} bg={bg}>
                <Tab fontWeight="700" fontSize="sm" py={3} _selected={{ color: `${zone.color}.500`, borderBottomWidth: '3px', borderColor: `${zone.color}.500` }}>
                    Overview
                </Tab>
                <Tab fontWeight="700" fontSize="sm" py={3} _selected={{ color: `${zone.color}.500`, borderBottomWidth: '3px', borderColor: `${zone.color}.500` }}>
                  Active Issues <Badge ml={2} colorScheme={zone.color} borderRadius="full">{issues.length}</Badge>
                </Tab>
                <Tab fontWeight="700" fontSize="sm" py={3} _selected={{ color: `${zone.color}.500`, borderBottomWidth: '3px', borderColor: `${zone.color}.500` }}>
                  Infrastructure <Badge ml={2} colorScheme="blue" borderRadius="full">{assets.length}</Badge>
                </Tab>
                <Tab fontWeight="700" fontSize="sm" py={3} _selected={{ color: `${zone.color}.500`, borderBottomWidth: '3px', borderColor: `${zone.color}.500` }}>
                  Work Orders <Badge ml={2} colorScheme="orange" borderRadius="full">{work_orders.length}</Badge>
                </Tab>
              </TabList>

              <TabPanels>
                {/* ==============================================================
                    TAB 1: OVERVIEW & KPIs
                ============================================================== */}
                <TabPanel p={6}>
                  {isLoading ? <LoadingSkeleton /> : (
                    <MotionBox initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
                      
                      {/* KPI Grid */}
                      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={8}>
                        <Stat p={5} bg={bg} borderWidth="1px" borderColor={border} borderRadius="xl" boxShadow="sm">
                          <StatLabel fontSize="xs" color={textMuted} fontWeight="700" textTransform="uppercase">Monitored Assets</StatLabel>
                          <StatNumber fontSize="3xl" fontWeight="800">{kpis?.total_assets || 0}</StatNumber>
                          <StatHelpText mb={0} fontWeight="500"><Icon as={MdEngineering} mr={1}/>Wells, Pumps & Pipes</StatHelpText>
                        </Stat>
                        
                        <Stat p={5} bg={bg} borderWidth="1px" borderColor={border} borderRadius="xl" boxShadow="sm">
                          <StatLabel fontSize="xs" color={textMuted} fontWeight="700" textTransform="uppercase">Avg Health Score</StatLabel>
                          <StatNumber fontSize="3xl" color="green.500" fontWeight="800">{kpis?.health_score || 100}%</StatNumber>
                          <StatHelpText mb={0} fontWeight="500"><Icon as={MdCheckCircle} mr={1} color="green.500"/>System Status</StatHelpText>
                        </Stat>
                        
                        <Stat p={5} bg={bg} borderWidth="1px" borderColor={border} borderRadius="xl" boxShadow="sm">
                          <StatLabel fontSize="xs" color={textMuted} fontWeight="700" textTransform="uppercase">Active Work Orders</StatLabel>
                          <StatNumber fontSize="3xl" color="blue.500" fontWeight="800">{kpis?.active_wos || 0}</StatNumber>
                          <StatHelpText mb={0} fontWeight="500"><Icon as={MdAssignment} mr={1} color="blue.500"/>Dispatched</StatHelpText>
                        </Stat>
                        
                        <Stat p={5} bg={statRedBg} borderWidth="1px" borderColor="red.200" borderRadius="xl" boxShadow="sm">
                          <StatLabel fontSize="xs" color="red.600" fontWeight="700" textTransform="uppercase">Critical Alerts</StatLabel>
                          <StatNumber fontSize="3xl" color="red.600" fontWeight="800">{kpis?.critical_alerts || 0}</StatNumber>
                          <StatHelpText mb={0} color="red.500" fontWeight="500"><Icon as={MdWarning} mr={1}/>Immediate Action</StatHelpText>
                        </Stat>
                      </SimpleGrid>

                      {/* Issue Composition & Map Details */}
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                        <Box bg={bg} p={6} borderRadius="xl" borderWidth="1px" borderColor={border} boxShadow="sm">
                          <Heading size="sm" mb={5} fontWeight="700">Issue Composition</Heading>
                          {issue_composition.length === 0 ? (
                            <EmptyState icon={MdCheckCircle} title="All Clear" message="No active issues in this zone." />
                          ) : (
                            <VStack align="stretch" spacing={4}>
                              {issue_composition.map((iss, i) => {
                                const cfg = ISSUE_TYPE_CONFIG[iss.type?.toLowerCase()] || { color: iss.color || 'gray', icon: MdErrorOutline };
                                const pct = zone.count > 0 ? (iss.count / zone.count) * 100 : 0;
                                return (
                                  <Box key={i}>
                                    <Flex justify="space-between" mb={1} align="center">
                                      <HStack spacing={2}>
                                        <Icon as={cfg.icon} color={`${cfg.color}.500`} boxSize={4}/>
                                        <Text fontSize="sm" fontWeight="600">{iss.type}</Text>
                                      </HStack>
                                      <HStack spacing={2}>
                                        <Text fontSize="sm" fontWeight="800">{iss.count}</Text>
                                        <Text fontSize="xs" color={textMuted}>({pct.toFixed(0)}%)</Text>
                                      </HStack>
                                    </Flex>
                                    <Progress value={pct} size="sm" colorScheme={cfg.color} borderRadius="full" bg={progressBg} />
                                  </Box>
                                );
                              })}
                            </VStack>
                          )}
                        </Box>

                        <Box bg={bg} p={6} borderRadius="xl" borderWidth="1px" borderColor={border} boxShadow="sm" display="flex" flexDirection="column" justifyContent="center" alignItems="center" textAlign="center">
                            <Circle size="64px" bg="blue.50" mb={4}>
                                <Icon as={MdMap} boxSize={8} color="blue.500" />
                            </Circle>
                            <Heading size="sm" mb={2}>Spatial Analysis</Heading>
                            <Text fontSize="sm" color={textMuted} mb={4} maxW="80%">
                                The system has successfully isolated {zone.count} issues bounded within the {zone.area} coordinate polygon.
                            </Text>
                            <Button rightIcon={<Icon as={MdArrowForward} />} colorScheme={zone.color} variant="outline" size="sm" onClick={onClose}>
                                Focus Map on Zone
                            </Button>
                        </Box>
                      </SimpleGrid>
                    </MotionBox>
                  )}
                </TabPanel>

                {/* ==============================================================
                    TAB 2: ACTIVE ISSUES
                ============================================================== */}
                <TabPanel p={6}>
                  <Flex justify="space-between" align="center" mb={4}>
                    <Heading size="sm" fontWeight="700">Pre-Filtered Issues</Heading>
                    <InputGroup maxW="300px" size="sm">
                        <InputLeftElement><Icon as={MdSearch} color="gray.400" /></InputLeftElement>
                        <Input 
                            placeholder="Search issues..." 
                            value={issueSearch} 
                            onChange={(e) => setIssueSearch(e.target.value)}
                            borderRadius="lg"
                            bg={bg}
                        />
                    </InputGroup>
                  </Flex>
                  
                  {isLoading ? <LoadingSkeleton /> : filteredIssues.length === 0 ? (
                    <EmptyState icon={MdCheckCircle} title="No Issues" message="There are no active issues matching your search in this zone." />
                  ) : (
                    <MotionVStack spacing={3} align="stretch" variants={containerVariants} initial="hidden" animate="visible">
                      {filteredIssues.map(issue => (
                        <IssueRow key={issue.id} issue={issue} />
                      ))}
                    </MotionVStack>
                  )}
                </TabPanel>

                {/* ==============================================================
                    TAB 3: ASSETS (Infrastructure)
                ============================================================== */}
                <TabPanel p={6}>
                  <Flex justify="space-between" align="center" mb={4}>
                    <Heading size="sm" fontWeight="700">Zone Infrastructure</Heading>
                    <InputGroup maxW="300px" size="sm">
                        <InputLeftElement><Icon as={MdSearch} color="gray.400" /></InputLeftElement>
                        <Input 
                            placeholder="Search assets (Wells, Pumps)..." 
                            value={assetSearch} 
                            onChange={(e) => setAssetSearch(e.target.value)}
                            borderRadius="lg"
                            bg={bg}
                        />
                    </InputGroup>
                  </Flex>

                  {isLoading ? <LoadingSkeleton /> : filteredAssets.length === 0 ? (
                    <EmptyState icon={MdEngineering} title="No Assets Found" message="There are no registered assets in this zone matching your search." />
                  ) : (
                    <MotionVStack spacing={3} align="stretch" variants={containerVariants} initial="hidden" animate="visible">
                      {filteredAssets.map(asset => (
                        <AssetRow key={asset.id} asset={asset} />
                      ))}
                    </MotionVStack>
                  )}
                </TabPanel>

                {/* ==============================================================
                    TAB 4: WORK ORDERS
                ============================================================== */}
                <TabPanel p={6}>
                  <Heading size="sm" fontWeight="700" mb={4}>Dispatched Work Orders</Heading>
                  {isLoading ? <LoadingSkeleton /> : work_orders.length === 0 ? (
                    <EmptyState icon={MdCheckCircle} title="No Active Dispatches" message="There are currently no active work orders dispatched to this zone." />
                  ) : (
                    <MotionVStack spacing={3} align="stretch" variants={containerVariants} initial="hidden" animate="visible">
                      {work_orders.map(wo => (
                        <WorkOrderRow key={wo.id} wo={wo} />
                      ))}
                    </MotionVStack>
                  )}
                </TabPanel>

              </TabPanels>
            </Tabs>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ZoneDetailModal;